import { useRef, useState, useCallback } from "react";

/**
 * useVideoRecording — encapsulates all video recording logic including
 * canvas-based cropping to 9:16, MediaRecorder codec negotiation,
 * and blob creation.
 *
 * Expects refs/state from the parent Camera for the live stream.
 */
export default function useVideoRecording({
	streamRef,
	videoRef,
	facingMode,
	isMuted,
	showPreview,
	onCapture,
	onClose,
	setStream,
	setError,
	duckBgm,
	restoreBgm,
}) {
	const mediaRecorderRef = useRef(null);
	const recordedChunksRef = useRef([]);
	const [isRecording, setIsRecording] = useState(false);
	const [capturedVideo, setCapturedVideo] = useState(null);
	const [isUploadedVideo, setIsUploadedVideo] = useState(false);

	// Refs to avoid stale closures in mediaRecorder.onstop
	const showPreviewRef = useRef(showPreview);
	const onCaptureRef = useRef(onCapture);
	const onCloseRef = useRef(onClose);
	showPreviewRef.current = showPreview;
	onCaptureRef.current = onCapture;
	onCloseRef.current = onClose;

	const startRecording = useCallback(() => {
		if (!streamRef.current || !videoRef.current) {
			return;
		}

		recordedChunksRef.current = [];

		try {
			const video = videoRef.current;
			const canvas = document.createElement("canvas");

			const targetWidth = 480;
			const targetHeight = 854;
			canvas.width = targetWidth;
			canvas.height = targetHeight;

			const ctx = canvas.getContext("2d");

			const videoAspect = video.videoWidth / video.videoHeight;
			const targetAspect = 9 / 16;

			let sourceWidth, sourceHeight, sourceX, sourceY;

			if (videoAspect > targetAspect) {
				sourceHeight = video.videoHeight;
				sourceWidth = sourceHeight * targetAspect;
				sourceX = (video.videoWidth - sourceWidth) / 2;
				sourceY = 0;
			} else {
				sourceWidth = video.videoWidth;
				sourceHeight = sourceWidth / targetAspect;
				sourceX = 0;
				sourceY = (video.videoHeight - sourceHeight) / 2;
			}

			const fps = 30;
			const captureInterval = setInterval(() => {
				if (facingMode === "user") {
					ctx.save();
					ctx.translate(targetWidth, 0);
					ctx.scale(-1, 1);
				}

				ctx.drawImage(
					video,
					sourceX,
					sourceY,
					sourceWidth,
					sourceHeight,
					0,
					0,
					targetWidth,
					targetHeight
				);

				if (facingMode === "user") {
					ctx.restore();
				}
			}, 1000 / fps);

			const canvasStream = canvas.captureStream(fps);

			const audioTracks = streamRef.current?.getAudioTracks() || [];
			if (audioTracks.length > 0 && !isMuted) {
				canvasStream.addTrack(audioTracks[0]);
			}

			let options = {
				mimeType: "video/webm;codecs=vp8",
				videoBitsPerSecond: 2500000,
			};
			if (!MediaRecorder.isTypeSupported(options.mimeType)) {
				options = {
					mimeType: "video/webm;codecs=vp9",
					videoBitsPerSecond: 2000000,
				};
			}
			if (!MediaRecorder.isTypeSupported(options.mimeType)) {
				options = {
					mimeType: "video/webm",
					videoBitsPerSecond: 2500000,
				};
			}

			const mediaRecorder = new MediaRecorder(canvasStream, options);
			mediaRecorder.captureInterval = captureInterval;
			mediaRecorder.canvas = canvas;

			mediaRecorder.ondataavailable = (event) => {
				if (event.data && event.data.size > 0) {
					recordedChunksRef.current.push(event.data);
				}
			};

			mediaRecorder.onstop = () => {
				restoreBgm();

				setTimeout(() => {
					if (recordedChunksRef.current.length === 0) {
						if (import.meta.env.DEV) {
							console.error("No video data recorded");
						}
						setError("No video data was recorded. Please try again.");
						return;
					}

					const mimeType = mediaRecorder.mimeType || "video/webm";
					const blob = new Blob(recordedChunksRef.current, { type: mimeType });

					if (import.meta.env.DEV) {
						console.log(
							"Video blob created:",
							blob.size,
							"bytes, type:",
							mimeType
						);
					}

					if (blob.size === 0) {
						setError("Video recording is empty. Please try again.");
						return;
					}

					if (streamRef.current) {
						streamRef.current.getTracks().forEach((track) => track.stop());
						streamRef.current = null;
						setStream(null);
					}

					if (mediaRecorder.captureInterval) {
						clearInterval(mediaRecorder.captureInterval);
					}

					const videoUrl = URL.createObjectURL(blob);

					setTimeout(() => {
						setCapturedVideo(videoUrl);
						setIsUploadedVideo(false);
					}, 200);

					if (!showPreviewRef.current) {
						const file = new File([blob], `video-${Date.now()}.webm`, {
							type: mimeType,
						});
						onCaptureRef.current(file);
						onCloseRef.current();
					}
				}, 300);
			};

			mediaRecorder.start(100);
			mediaRecorderRef.current = mediaRecorder;
			setIsRecording(true);

			if (import.meta.env.DEV) {
				console.log("Recording started with mimeType:", options.mimeType);
			}

			duckBgm(0);
		} catch (err) {
			console.error("Error starting recording:", err);
			setError("Unable to start recording. Please try again.");
		}
	}, [
		streamRef,
		videoRef,
		facingMode,
		isMuted,
		setStream,
		setError,
		duckBgm,
		restoreBgm,
	]);

	const stopRecording = useCallback(() => {
		if (mediaRecorderRef.current && isRecording) {
			if (mediaRecorderRef.current.state === "recording") {
				mediaRecorderRef.current.requestData();
			}

			if (mediaRecorderRef.current.captureInterval) {
				clearInterval(mediaRecorderRef.current.captureInterval);
			}

			mediaRecorderRef.current.stop();
			setIsRecording(false);
		}
	}, [isRecording]);

	const clearCapturedVideo = useCallback(() => {
		if (capturedVideo) {
			URL.revokeObjectURL(capturedVideo);
		}
		setCapturedVideo(null);
		setIsUploadedVideo(false);
	}, [capturedVideo]);

	const setUploadedVideo = useCallback((url) => {
		setCapturedVideo(url);
		setIsUploadedVideo(true);
	}, []);

	return {
		isRecording,
		capturedVideo,
		isUploadedVideo,
		startRecording,
		stopRecording,
		clearCapturedVideo,
		setCapturedVideo,
		setUploadedVideo,
	};
}
